import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { connect } from "react-redux";
import BarLoader from "react-spinners/BarLoader";
import { setAlert } from "../../actions/alert";
import { useJobStatus } from "../../hooks/useJobStatus";
import { handleScraperError } from "../../utils/errorHandler";
import styled from "styled-components";

const ProcessingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 40px 20px;
  background-color: #f5f5f5;
`;

const ProcessingContent = styled.div`
  max-width: 600px;
  width: 100%;
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #212121;
  margin-bottom: 8px;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #757575;
  margin-bottom: 32px;
  text-align: center;
`;

const LoadingContainer = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

const ProgressMessage = styled.div`
  text-align: center;
  margin-top: 16px;
  font-size: 14px;
  color: #757575;
`;

const ErrorContainer = styled.div`
  margin-top: 24px;
  padding: 16px;
  background-color: #ffebee;
  border-radius: 8px;
  border-left: 4px solid #d32f2f;
`;

const ErrorTitle = styled.h3`
  font-size: 18px;
  color: #d32f2f;
  margin-bottom: 8px;
`;

const ErrorMessage = styled.p`
  font-size: 14px;
  color: #757575;
  margin-bottom: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s;

  &.primary {
    background-color: var(--color-primary);
    color: white;

    &:hover {
      background-color: var(--color-primary-light);
    }
  }

  &.secondary {
    background-color: #e0e0e0;
    color: #212121;

    &:hover {
      background-color: #d0d0d0;
    }
  }
`;

const ProcessingPage = ({ setAlert }) => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [noResults, setNoResults] = useState(false);
  const [jobResultData, setJobResultData] = useState(null);

  const {
    status: jobStatus,
    progress: jobProgress,
    progressMessage: jobProgressMessage,
    result: jobResult,
    error: jobError,
    timedOut: jobTimedOut,
  } = useJobStatus(jobId, {
    onComplete: (result) => {
      console.log("[ProcessingPage] Job completed, result received:", {
        hasWebsite: !!result?.website,
        website: result?.website,
        allKeys: result ? Object.keys(result) : 'null',
        hasPredictedOptions: !!result?.predictedOptions
      });

      const po = result?.predictedOptions;
      const hasItems = po &&
        (po.GUARANTEED_VEGAN?.length > 0 ||
         po.PROBABLY_VEGAN?.length > 0 ||
         po.MAYBE_VEGAN?.length > 0 ||
         po.PROBABLY_NOT_VEGAN?.length > 0);
      if (hasItems) {
        navigate("/create/v2/cuisine", { state: result });
      } else {
        // No menu items found — stay on this page and show options
        setJobResultData(result);
        setNoResults(true);
      }
    },
    onError: (error) => {
      // Error is handled in the render below
    },
    autoConnect: !!jobId,
  });

  const handleRetry = () => {
    navigate("/");
  };

  const handleManualEntry = () => {
    // Go to address page with whatever data we have from the scraper
    navigate("/create/v2/address", { state: jobResultData || {} });
  };

  if (jobError || jobTimedOut) {
    return (
      <ProcessingContainer>
        <ProcessingContent>
          <Title>Processing Failed</Title>
          <ErrorContainer>
            <ErrorTitle>{jobTimedOut ? "Timed Out" : "Error"}</ErrorTitle>
            <ErrorMessage>
              {jobTimedOut
                ? "The menu analysis is taking too long. The server may not be responding."
                : jobError || "An error occurred while processing. Please try again."}
            </ErrorMessage>
            <ButtonGroup>
              <Button className="primary" onClick={handleRetry}>
                Go Back
              </Button>
              <Button className="secondary" onClick={handleManualEntry}>
                Enter Manually
              </Button>
            </ButtonGroup>
          </ErrorContainer>
        </ProcessingContent>
      </ProcessingContainer>
    );
  }

  if (noResults) {
    return (
      <ProcessingContainer>
        <ProcessingContent>
          <Title>No Menu Items Found</Title>
          <ErrorContainer>
            <ErrorMessage>
              We couldn't extract menu items from this restaurant's website.
              You can go back and try a different restaurant, or enter the details manually.
            </ErrorMessage>
            <ButtonGroup>
              <Button className="primary" onClick={handleRetry}>
                Go Back
              </Button>
              <Button className="secondary" onClick={handleManualEntry}>
                Enter Manually
              </Button>
            </ButtonGroup>
          </ErrorContainer>
        </ProcessingContent>
      </ProcessingContainer>
    );
  }

  return (
    <ProcessingContainer>
      <ProcessingContent>
        <Title>Processing Menu</Title>
        <Subtitle>Please wait while we extract menu information...</Subtitle>
        <LoadingContainer>
          <BarLoader
            loading={true}
            color="var(--color-primary)"
            height="8px"
            cssOverride={{ margin: "0 auto", width: "100%" }}
            aria-label="Processing menu"
          />
          <ProgressMessage>
            {jobProgressMessage || "Analyzing menu..."}{" "}
            {jobProgress > 0 && `(${jobProgress}%)`}
          </ProgressMessage>
        </LoadingContainer>
      </ProcessingContent>
    </ProcessingContainer>
  );
};

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, { setAlert })(ProcessingPage);


